import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { trackPageView } from './utils/analytics';
import './AnalyticsCharts.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AnalyticsCharts = ({ authToken }) => {
  const [userPeriod, setUserPeriod] = useState('month');
  const [visitPeriod, setVisitPeriod] = useState('month');
  const [restaurantPeriod, setRestaurantPeriod] = useState('month');
  const [userData, setUserData] = useState([]);
  const [visitData, setVisitData] = useState([]);
  const [restaurantData, setRestaurantData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track page view when Analytics Dashboard is loaded
  useEffect(() => {
    trackPageView('/admin/analytics', 'Analytics Dashboard');
  }, []);

  useEffect(() => {
    fetchUserAnalytics();
  }, [userPeriod]);

  useEffect(() => {
    fetchVisitAnalytics();
  }, [visitPeriod]);

  useEffect(() => {
    fetchRestaurantAnalytics();
  }, [restaurantPeriod]);

  const fetchUserAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/users?period=${userPeriod}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user analytics');
      }

      const data = await response.json();
      setUserData(data);
    } catch (err) {
      console.error('Error fetching user analytics:', err);
    }
  };

  const fetchVisitAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/visits?period=${visitPeriod}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch visit analytics');
      }

      const data = await response.json();
      setVisitData(data);
    } catch (err) {
      console.error('Error fetching visit analytics:', err);
    } finally {
      setLoading(false);
    }
  };


  const fetchRestaurantAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/restaurants?period=${restaurantPeriod}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch restaurant analytics');
      }

      const data = await response.json();
      setRestaurantData(data);
    } catch (err) {
      console.error('Error fetching restaurant analytics:', err);
    }
  };


  const formatXAxis = (value, period) => {
    if (period === 'day') {
      return value.substring(5); // Show MM-DD
    } else if (period === 'week') {
      return value.substring(2); // Show YY-Wxx
    } else if (period === 'month') {
      return value.substring(5); // Show MM
    }
    return value;
  };


  return (
    <div className="analytics-section">
      <div className="analytics-header">
        <h2 className="analytics-title">📊 Analytics Dashboard</h2>
      </div>

      <div className="charts-container">
        {/* User Registrations Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">👥 New User Registrations</h3>
            <div className="period-selector">
              <button 
                className={`period-btn ${userPeriod === 'day' ? 'active' : ''}`}
                onClick={() => setUserPeriod('day')}
              >
                Daily
              </button>
              <button 
                className={`period-btn ${userPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setUserPeriod('week')}
              >
                Weekly
              </button>
              <button 
                className={`period-btn ${userPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setUserPeriod('month')}
              >
                Monthly
              </button>
              <button 
                className={`period-btn ${userPeriod === 'year' ? 'active' : ''}`}
                onClick={() => setUserPeriod('year')}
              >
                Yearly
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="chart-loading">Loading...</div>
          ) : userData.length === 0 ? (
            <div className="chart-empty">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={userData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => formatXAxis(value, userPeriod)}
                  stroke="#64748b"
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  name="New Users"
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
          
          <div className="chart-summary">
            <span className="summary-label">Total:</span>
            <span className="summary-value">
              {userData.reduce((sum, item) => sum + item.count, 0)} users
            </span>
          </div>
        </div>

        {/* Visits Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">🍽️ Restaurant Visits</h3>
            <div className="period-selector">
              <button 
                className={`period-btn ${visitPeriod === 'day' ? 'active' : ''}`}
                onClick={() => setVisitPeriod('day')}
              >
                Daily
              </button>
              <button 
                className={`period-btn ${visitPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setVisitPeriod('week')}
              >
                Weekly
              </button>
              <button 
                className={`period-btn ${visitPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setVisitPeriod('month')}
              >
                Monthly
              </button>
              <button 
                className={`period-btn ${visitPeriod === 'year' ? 'active' : ''}`}
                onClick={() => setVisitPeriod('year')}
              >
                Yearly
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="chart-loading">Loading...</div>
          ) : visitData.length === 0 ? (
            <div className="chart-empty">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={visitData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => formatXAxis(value, visitPeriod)}
                  stroke="#64748b"
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  name="Visits"
                  fill="#10b981" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
          
          <div className="chart-summary">
            <span className="summary-label">Total:</span>
            <span className="summary-value">
              {visitData.reduce((sum, item) => sum + item.count, 0)} visits
            </span>
          </div>
        </div>

        {/* Restaurants Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">🏪 New Restaurants</h3>
            <div className="period-selector">
              <button 
                className={`period-btn ${restaurantPeriod === 'day' ? 'active' : ''}`}
                onClick={() => setRestaurantPeriod('day')}
              >
                Daily
              </button>
              <button 
                className={`period-btn ${restaurantPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setRestaurantPeriod('week')}
              >
                Weekly
              </button>
              <button 
                className={`period-btn ${restaurantPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setRestaurantPeriod('month')}
              >
                Monthly
              </button>
              <button 
                className={`period-btn ${restaurantPeriod === 'year' ? 'active' : ''}`}
                onClick={() => setRestaurantPeriod('year')}
              >
                Yearly
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="chart-loading">Loading...</div>
          ) : restaurantData.length === 0 ? (
            <div className="chart-empty">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={restaurantData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => formatXAxis(value, restaurantPeriod)}
                  stroke="#64748b"
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  name="New Restaurants"
                  fill="#f59e0b" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
          
          <div className="chart-summary">
            <span className="summary-label">Total:</span>
            <span className="summary-value">
              {restaurantData.reduce((sum, item) => sum + item.count, 0)} restaurants
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
